import { WrenchIcon } from "@heroicons/react/24/solid";
import { FeatureFlagGate } from "@utils/FeatureFlags/FeatureFlagGate";
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags.ts";
import { hasTemplatesAccess } from "@utils/FeatureFlags/selfServeAccess";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Navigate, Outlet, createBrowserRouter, useLocation, useParams } from "react-router-dom";

import { Small } from "@fronterahealth/frontera-ui-components";

import { Authorize, NotAllowedPage } from "@components/Authorize/Authorize.tsx";
import { RouteErrorElement } from "@components/Error/RouteErrorElement.tsx";
import { AssessmentReportV2Details } from "@pages/AssessmentReportV2Details/AssessmentReportDetails.tsx";
import { ClientDetails as AssessmentReportV2ClientDetails } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/ClientDetails.tsx";
import { Prioritize as PrioritizeV2 } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/Prioritize.tsx";
import { ReportSummaryPart1 as ReportSummaryPart1V2 } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/ReportSummaries/ReportSummaryPart1/ReportSummaryPart1.tsx";
import { ReportSummaryPart2 as ReportSummaryPart2V2 } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/ReportSummaries/ReportSummaryPart2/ReportSummaryPart2.tsx";
import { ReviewAndSign as ReviewAndSignV2 } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/ReviewAndSign.tsx";
import { ShortTermGoals as ShortTermGoalsV2 } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/ShortTermGoals.tsx";
import { UploadFiles as UploadFilesV2 } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/UploadFiles/UploadFiles.tsx";
import { ClientDetails } from "@pages/ClientDetails/ClientDetails.tsx";
import { ClientDetailsAvailability } from "@pages/ClientDetailsSubPages/Availability.tsx";
import { BasicInfo } from "@pages/ClientDetailsSubPages/BasicInfo.tsx";
import { ClientDetailsCaregivers } from "@pages/ClientDetailsSubPages/Caregivers.tsx";
import { ClientIntakeDetails } from "@pages/ClientDetailsSubPages/ClientIntakeDetails/ClientIntakeDetails.tsx";
import { ClientProviders } from "@pages/ClientDetailsSubPages/ClientProviders/ClientProviders.tsx";
import { ClientDetailsDetails } from "@pages/ClientDetailsSubPages/Details.tsx";
import { ClientDetailsEHRData } from "@pages/ClientDetailsSubPages/EHRData.tsx";
import { FileLibrary } from "@pages/ClientDetailsSubPages/FileLibrary.tsx";
import { ClientDetailsFiles } from "@pages/ClientDetailsSubPages/Files.tsx";
import { ClientDetailsForms } from "@pages/ClientDetailsSubPages/Forms.tsx";
import { ClientDetailsInsurance } from "@pages/ClientDetailsSubPages/Insurance.tsx";
import { ClientDetailsFormsIntakeForm } from "@pages/ClientDetailsSubPages/IntakeForm/IntakeForm.tsx";
import { IntakeFormPageSwitcher } from "@pages/ClientDetailsSubPages/IntakeForm/IntakeFormPageSwitcher.tsx";
import { links } from "@pages/ClientDetailsSubPages/IntakeForm/Links/config.ts";
import { ClientEhrData } from "@pages/ClientEhrData/ClientEhrData.tsx";
import { ClientEhrSyncHistory } from "@pages/ClientEhrData/ClientEhrSyncHistory.tsx";
import { ClientEhrProgramming } from "@pages/ClientEhrProgramming/ClientEhrProgramming.tsx";
import { ClientProgramming } from "@pages/ClientProgramming/ClientProgramming.tsx";
import { AddClinic } from "@pages/Clinic/AddClinic.tsx";
import { ClinicDetail } from "@pages/Clinic/ClinicDetail.tsx";
import { Clinics } from "@pages/Clinic/Clinics.tsx";
import { EditClinic } from "@pages/Clinic/EditClinic.tsx";
import { LeadDetail } from "@pages/Clinic/LeadDetail.tsx";
import { Leads } from "@pages/Clinic/Leads.tsx";
import { ClinicalReportDetails } from "@pages/ClinicalReportDetails/ClinicalReportDetails.tsx";
import { LearningTreeReview } from "@pages/ClinicalReportDetails/LearningTreeReview/LearningTreeReview";
import { RefreshingData } from "@pages/ClinicalReportDetails/RefreshingData/RefreshingData.tsx";
import { NarrativePage } from "@pages/ClinicalReportDetails/ReportPage/NarrativePage.tsx";
import { ProgrammingPage } from "@pages/ClinicalReportDetails/ReportPage/ProgrammingPage.tsx";
import { ReportPage } from "@pages/ClinicalReportDetails/ReportPage/ReportPage.tsx";
import { ReviewAndSign } from "@pages/ClinicalReportDetails/ReviewAndSign/ReviewAndSign.tsx";
import { SelectProgramming } from "@pages/ClinicalReportDetails/SelectProgramming/SelectProgramming.tsx";
import { ClinicalReportUploadFiles } from "@pages/ClinicalReportDetails/UploadFiles/ClinicalReportUploadFiles.tsx";
import { EvaluationDetails } from "@pages/EvaluationDetails/EvaluationDetails.tsx";
import { EvaluationAppendix } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/EvaluationAppendix.tsx";
import { EvaluationClientDetails } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/EvaluationClientDetails.tsx";
import { EvaluationConclusions } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/EvaluationConclusions.tsx";
import { EvaluationRecommendations } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/EvaluationRecommendations.tsx";
import { EvaluationReportPart1 } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/EvaluationReportPart1.tsx";
import { EvaluationReportPart2 } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/EvaluationReportPart2.tsx";
import { EvaluationReviewAndSign } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/EvaluationReviewAndSign.tsx";
import { EvaluationUploadFiles } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/EvaluationUploadFiles/EvaluationUploadFiles.tsx";
import { BillingPage } from "@pages/FronteraClarity/BillingPage.tsx";
import { ExpertChatPage } from "@pages/FronteraClarity/ExpertChatPage.tsx";
import { FronteraClarity } from "@pages/FronteraClarity/FronteraClarity.tsx";
import { MyAvailabilityPage } from "@pages/FronteraClarity/MyAvailabilityPage.tsx";
import { ProfileSettings } from "@pages/FronteraClarity/ProfileSettings.tsx";
import { SchedulingPage } from "@pages/FronteraClarity/SchedulingPage.tsx";
import { GoalBankImportPage } from "@pages/GoalBankImport/GoalBankImportPage.tsx";
import { HomePage } from "@pages/HomePage/HomePage.tsx";
import { UserIntegrationsPage } from "@pages/Integrations/UserIntegrationsPage.tsx";
import { CaregiverChildrenTab } from "@pages/Members/CaregiverDetail/CaregiverChildrenTab";
import { CaregiverDetail } from "@pages/Members/CaregiverDetail/CaregiverDetail";
import { CaregiverDetailsTab } from "@pages/Members/CaregiverDetail/CaregiverDetailsTab";
import { Members } from "@pages/Members/Members";
import { ProviderClientsTab } from "@pages/Members/ProviderDetail/ProviderClientsTab";
import { ProviderDetail } from "@pages/Members/ProviderDetail/ProviderDetail";
import { ProviderDetailsTab as MemberProviderDetailsTab } from "@pages/Members/ProviderDetail/ProviderDetailsTab";
import { NewClient } from "@pages/NewClient/NewClient.tsx";
import { NewClientLite } from "@pages/NewClient/NewClientLite/NewClientLite.tsx";
import { OrgManageNotificationsPage } from "@pages/Notifications/OrgManageNotificationsPage.tsx";
import { UserManageNotificationsPage } from "@pages/Notifications/UserManageNotificationsPage.tsx";
import { OnboardingPage } from "@pages/Onboarding/OnboardingPage.tsx";
import { OrgQASettings } from "@pages/QASettings/OrgQASettings.tsx";
import { ReportTemplateQASettings } from "@pages/QASettings/ReportTemplateQASettings.tsx";
import { ReleaseNotesPage } from "@pages/ReleaseNotesPage/ReleaseNotesPage.tsx";
import { SignatureSettings } from "@pages/SignatureSettings/SignatureSettings.tsx";
import { TemplateEditor } from "@pages/Templates/TemplateEditor.tsx";
import { VideoAnalytics } from "@pages/VideoAnalytics/VideoAnalytics.tsx";
import { VideoSessionDetails } from "@pages/VideoSessions/VideoSessionDetails.tsx";
import { VideoSessionsSummary } from "@pages/VideoSessions/VideoSessionsSummary.tsx";
import { VideoSessionDetailsV2 } from "@pages/VideoSessionsV2/VideoSessionDetails.tsx";
import { VideoSessionsSummaryV2 } from "@pages/VideoSessionsV2/VideoSessionsSummary.tsx";
import { useAdminData } from "@providers/AdminDataProvider.tsx";
import { AssessmentBuilderProvider as AssessmentBuilderV2Provider } from "@providers/AssessmentBuilderV2Provider.tsx";
import { ClientProgrammingProvider } from "@providers/ClientProgrammingProvider.tsx";
import { ClientProvider } from "@providers/ClientProvider.tsx";
import { ClinicalReportDetailsProvider } from "@providers/ClinicalReportDetailsProvider.tsx";
import { EHRClientProvider } from "@providers/EHRClientProvider.tsx";
import { EvaluationProvider } from "@providers/EvaluationProvider.tsx";
import { ProvidersDataProvider } from "@providers/ProvidersDataProvider.tsx";
import { TemplateEditorProvider } from "@providers/TemplateEditorProvider.tsx";
import { TemplateProvider } from "@providers/TemplateProvider.tsx";
import { VideoAnalyticsProvider } from "@providers/VideoAnalyticsProvider.tsx";
import { VideoSessionProvider } from "@providers/VideoSessionProvider.tsx";
import { VideoSessionProviderV2 } from "@providers/VideoSessionProviderV2.tsx";
import { UserSettings } from "@routes/AccountSettings/Settings.tsx";
import { AdminAnalyticsRoute } from "@routes/AdminAnalytics/AdminAnalyticsRoute.tsx";
import { AssessmentReportsV2 } from "@routes/AssessmentReportsV2/AssessmentReports.tsx";
import { AssessmentReportV2DetailsRedirect } from "@routes/AssessmentReportsV2/AssessmentReportsHelper.tsx";
import { ClientDashboard } from "@routes/ClientDashboard/ClientDashboard.tsx";
import { ClinicalReports, buildIaMergeRedirectPath } from "@routes/ClinicalReports/ClinicalReports";
import {
  ClinicalReportDetailsRedirect,
  ClinicalReportRedirect,
} from "@routes/ClinicalReports/ClinicalReportsHelper.tsx";
import { Downloads } from "@routes/Downloads.tsx";
import { Evaluations } from "@routes/Evaluations/Evaluations.tsx";
import { EvaluationDetailsRedirect } from "@routes/Evaluations/EvaluationsHelper.tsx";
import { Login } from "@routes/Login.tsx";
import { Checkout } from "@routes/Payment/Checkout.tsx";
import { Products } from "@routes/Payment/Products.tsx";
import Templates from "@routes/Templates/Templates.tsx";

import App from "./App.tsx";
import "./index.css";
import { homeRolesForFlags, roleMapping } from "./layouts/config.ts";

export const ComingSoon = () => (
  <div className="flex flex-col">
    <Small className="flex items-center">
      <WrenchIcon className="mr-2 h-6 w-6" /> Under Construction
    </Small>
  </div>
);

const ClientBasicInfoRedirect = () => {
  const { state } = useLocation();
  return <Navigate to="basic-info" replace state={state} />;
};

const ClientOverviewRedirect = () => {
  const { state } = useLocation();
  return <Navigate to="overview" replace state={state} />;
};

const ClientEhrDataRedirect = () => {
  const { state } = useLocation();
  return <Navigate to="programming" replace state={state} />;
};

const VideoSessionsRedirect = () => {
  const flags = useFlags();

  if (!flags["video-tooling"]) return <NotAllowedPage />;
  else return <VideoSessionsSummary />;
};

const VideoSessionsV2Redirect = () => {
  const flags = useFlags();

  if (!flags["video-tooling"]) return <NotAllowedPage />;
  else return <VideoSessionsSummaryV2 />;
};

const AnalyticsRedirect = () => {
  const flags = useFlags();

  if (!flags["analytic-dashboard-ui"]) return <NotAllowedPage />;
  else return <VideoAnalytics />;
};

const ProgrammingRedirect = () => {
  const flags = useFlags<FeatureFlags>();

  if (!flags["treatment-plan-upload"]) return <NotAllowedPage />;
  else return <ClientProgramming />;
};

const TemplatesRedirect = () => {
  const flags = useFlags<FeatureFlags>();

  if (!hasTemplatesAccess(flags)) return <NotAllowedPage />;
  return <Templates />;
};

const ClinicalReportsRedirect = () => {
  const flags = useFlags();

  if (!flags["reassessment-builder"] && !flags["ia-clinical-merge"]) return <NotAllowedPage />;
  else return <Outlet />;
};

const UserIntegrationsRedirect = () => {
  const flags = useFlags();
  if (!flags["user-integrations"]) return <NotAllowedPage />;
  else return <UserIntegrationsPage />;
};

const DbSinglePageRedirect = () => {
  const flags = useFlags();
  if (!flags["db-single-page"]) return <NotAllowedPage />;
  else return <ClinicalReportDetails />;
};

const IaClinicalMergeReportRedirect = () => {
  const flags = useFlags<FeatureFlags>();
  if (!flags["ia-clinical-merge"]) return <NotAllowedPage />;
  return (
    <AssessmentBuilderV2Provider>
      <AssessmentReportV2Details />
    </AssessmentBuilderV2Provider>
  );
};

// Redirects /assessment-reports (list only) → /clinical-reports when ia-clinical-merge is on.
// Sub-paths (e.g. /assessment-reports/:id/*) fall through to AssessmentReportsIAMergeRedirect
// via the child routes, so we must NOT redirect those here — only the exact list path.
const AssessmentReportsListRedirect = () => {
  const flags = useFlags<FeatureFlags>();
  const { pathname } = useLocation();
  if (pathname === "/assessment-reports" && flags["ia-clinical-merge"]) {
    return <Navigate to="/clinical-reports" replace />;
  }
  return (
    <EHRClientProvider>
      <AssessmentReportsV2 />
    </EHRClientProvider>
  );
};

// Redirects /assessment-reports/:assessmentId/* → /clinical-reports/assessment-reports/:assessmentId/*
// Preserves the full sub-path so in-progress wizard steps and bookmarks continue to work.
const AssessmentReportsIAMergeRedirect = () => {
  const flags = useFlags<FeatureFlags>();
  const { assessmentId } = useParams();
  const { pathname } = useLocation();

  if (flags["ia-clinical-merge"]) {
    if (!assessmentId) {
      return <Navigate to="/clinical-reports" replace />;
    }
    return <Navigate to={buildIaMergeRedirectPath(pathname, assessmentId)} replace />;
  }

  return (
    <AssessmentBuilderV2Provider>
      <AssessmentReportV2Details />
    </AssessmentBuilderV2Provider>
  );
};

const CaregiverDetailRouteGate = () => {
  const flags = useFlags<FeatureFlags>();
  if (!flags["portal-caregiver-activation-invites"]) return <NotAllowedPage />;
  return <CaregiverDetail />;
};

// PROA-1081 Portal Home Page gate. The page is launched behind `portal-home-page`;
// until the flag is on, a user who reaches /home (e.g. a stale bookmark) is sent to
// "/" so the role-based landing redirect picks their normal page. The `?state=`
// QA override still works once the flag is on. Mirrors the EHRClientProvider wrap
// used by /clinical-reports so the start-a-report dialog works.
const HomePageRouteGate = () => {
  const flags = useFlags<FeatureFlags>();
  const { roles } = useAdminData();
  if (!flags["portal-home-page"]) return <Navigate to="/" replace />;
  // Psychologist-only users only get /home under db-single-page (homeRolesForFlags):
  // their diagnosis-eval report details live in the unified /clinical-reports tree,
  // which that flag gates. With it off, bounce to "/" — the root redirect resolves
  // psychologists to their legacy /evaluations landing. The route's <Authorize>
  // already blocks non-clinical roles; this refines it for the current flag state.
  const homeRoles = homeRolesForFlags(flags) as string[];
  if (!roles.some((r) => homeRoles.includes(r))) return <Navigate to="/" replace />;
  return (
    <EHRClientProvider>
      <HomePage />
    </EHRClientProvider>
  );
};

export const router = createBrowserRouter([
  {
    path: "/",
    /**
     * what gets rendered at / (the Root route)
     * The rest of the routes will render inside of it
     */
    // This will be the element that will handle all inbound traffic
    element: <App />,
    errorElement: <RouteErrorElement />,
    children: [
      {
        index: true,
        element: <NotAllowedPage />,
      },
      {
        path: "login",
        /**
         * what gets rendered at / (the Root route)
         * The rest of the routes will render inside of it
         */
        element: <Login />,
        errorElement: <RouteErrorElement />,
      },
      {
        path: "products",
        /**
         * what gets rendered at / (the Root route)
         * The rest of the routes will render inside of it
         */
        element: <Products />,
        errorElement: <RouteErrorElement />,
      },
      {
        path: "home",
        // PROA-1081 Portal Home Page. Gated behind `portal-home-page` (see
        // HomePageRouteGate): flag off → redirect to "/". State is auto-selected
        // from real signals; `?state=1a|1b|2|3` overrides it for review.
        // `redirect` on Authorize sends non-clinical roles (e.g. admin with a stale
        // bookmark) to "/" → their normal role-based landing, rather than a 403.
        element: (
          <Authorize allowedRoles={roleMapping["/home"]} redirect>
            <HomePageRouteGate />
          </Authorize>
        ),
        errorElement: <RouteErrorElement />,
      },
      {
        path: "release-notes",
        element: (
          <Authorize allowedRoles={roleMapping["/release-notes"]} redirect>
            <ReleaseNotesPage />
          </Authorize>
        ),
        errorElement: <RouteErrorElement />,
      },
      {
        path: "onboarding",
        // Provider onboarding flow. Renders full-screen (no sidebar): IndexLayout
        // skips its sidebar chrome for the "/onboarding" path. Step selected via `?step=` query param
        // (loader | profile | connect-ehr | import | create-client) so each
        // screen is reviewable by URL. Restricted to ONBOARDING_ROLES (all
        // providers; non-providers like caregiver / clarity are excluded). The
        // first-login redirect gate lives in IndexLayout (layouts/index.tsx).
        element: (
          <Authorize allowedRoles={roleMapping["/onboarding"]}>
            <OnboardingPage />
          </Authorize>
        ),
        errorElement: <RouteErrorElement />,
      },
      {
        path: "settings",
        /**
         * what gets rendered at / (the Root route)
         * The rest of the routes will render inside of it
         */
        element: <UserSettings />,
        errorElement: <RouteErrorElement />,
      },

      {
        path: "downloads",
        /**
         * what gets rendered at / (the Root route)
         * The rest of the routes will render inside of it
         */
        element: (
          <Authorize allowedRoles={roleMapping["/downloads"]}>
            <Downloads />
          </Authorize>
        ),
        errorElement: <RouteErrorElement />,
      },
      {
        path: "video-sessions",
        element: (
          <Authorize allowedRoles={roleMapping["/video-sessions"]}>
            <ProvidersDataProvider>
              <VideoSessionProvider>
                <VideoSessionsRedirect />
              </VideoSessionProvider>
            </ProvidersDataProvider>
          </Authorize>
        ),
        errorElement: <RouteErrorElement />,
        children: [
          {
            path: ":mediaId",
            element: <VideoSessionDetails />,
          },
        ],
      },
      {
        path: "video-sessions-v2",
        element: (
          <Authorize allowedRoles={roleMapping["/video-sessions"]}>
            <ProvidersDataProvider>
              <VideoSessionProviderV2>
                <VideoSessionsV2Redirect />
              </VideoSessionProviderV2>
            </ProvidersDataProvider>
          </Authorize>
        ),
        errorElement: <RouteErrorElement />,
        children: [
          {
            path: ":mediaId",
            element: <VideoSessionDetailsV2 />,
          },
        ],
      },
      {
        path: "analytics",
        element: (
          <Authorize allowedRoles={roleMapping["/analytics"]}>
            <VideoAnalyticsProvider>
              <AnalyticsRedirect />
            </VideoAnalyticsProvider>
          </Authorize>
        ),
        errorElement: <RouteErrorElement />,
      },
      {
        // Admin Analytics dashboard (PROA-1474). The Reports tab reads the
        // `adminAnalytics` GraphQL resource directly (contract in
        // `data/BACKEND_CONTRACT.md`); the adapter passed here only serves
        // the Activation tab, which has no backend resolver yet.
        path: "admin-analytics",
        element: <AdminAnalyticsRoute allowedRoles={roleMapping["/admin-analytics"]} />,
        errorElement: <RouteErrorElement />,
      },
      {
        path: "members",
        element: (
          <Authorize allowedRoles={roleMapping["/members"]}>
            <ProvidersDataProvider>
              <Outlet />
            </ProvidersDataProvider>
          </Authorize>
        ),
        errorElement: <RouteErrorElement />,
        children: [
          {
            index: true,
            element: <Members />,
          },
          {
            path: "provider/:providerId",
            element: <ProviderDetail />,
            children: [
              {
                path: "",
                element: <Navigate to="details" replace />,
              },
              {
                path: "details",
                element: <MemberProviderDetailsTab />,
              },
              {
                path: "clients",
                element: <ProviderClientsTab />,
              },
            ],
          },
          {
            path: "caregiver/:caregiverId",
            element: <CaregiverDetailRouteGate />,
            children: [
              {
                path: "",
                element: <Navigate to="details" replace />,
              },
              {
                path: "details",
                element: <CaregiverDetailsTab />,
              },
              {
                path: "children",
                element: <CaregiverChildrenTab />,
              },
            ],
          },
        ],
      },
      {
        path: "evaluations",
        element: (
          <Authorize allowedRoles={roleMapping["/evaluations"]}>
            <Outlet />
          </Authorize>
        ),
        errorElement: <RouteErrorElement />,
        children: [
          {
            index: true,
            element: <Evaluations />,
          },
          {
            path: ":evaluationId",
            element: (
              <EvaluationProvider>
                <EvaluationDetails />
              </EvaluationProvider>
            ),
            children: [
              {
                path: "",
                element: <EvaluationDetailsRedirect />,
              },
              {
                path: "client-details",
                element: <EvaluationClientDetails />,
              },
              {
                path: "upload-files",
                element: <EvaluationUploadFiles />,
              },
              {
                path: "report-part-1",
                element: <EvaluationReportPart1 />,
              },
              {
                path: "report-part-2",
                element: <EvaluationReportPart2 />,
              },
              {
                path: "conclusions",
                element: <EvaluationConclusions />,
              },
              {
                path: "recommendations",
                element: <EvaluationRecommendations />,
              },
              {
                path: "appendix",
                element: <EvaluationAppendix />,
              },
              {
                path: "review-and-sign",
                element: <EvaluationReviewAndSign />,
              },
            ],
          },
        ],
      },
      {
        path: "assessment-reports",
        element: (
          <Authorize allowedRoles={roleMapping["/assessment-reports"]}>
            <AssessmentReportsListRedirect />
          </Authorize>
        ),
        errorElement: <RouteErrorElement />,
        children: [
          {
            path: ":assessmentId",
            element: <AssessmentReportsIAMergeRedirect />,
            children: [
              {
                path: "",
                element: <AssessmentReportV2DetailsRedirect />,
              },
              {
                path: "client-details",
                element: <AssessmentReportV2ClientDetails />,
              },
              {
                path: "upload-files",
                element: <UploadFilesV2 />,
              },
              {
                path: "report-part-1",
                element: <ReportSummaryPart1V2 />,
              },
              {
                path: "prioritize",
                element: <PrioritizeV2 />,
              },
              {
                path: "short-term-goals",
                element: <ShortTermGoalsV2 />,
              },
              {
                path: "report-part-2",
                element: <ReportSummaryPart2V2 />,
              },
              {
                path: "review-and-sign",
                element: <ReviewAndSignV2 />,
              },
            ],
            errorElement: <RouteErrorElement />,
          },
        ],
      },
      {
        path: "clients",
        element: (
          <Authorize allowedRoles={roleMapping["/clients"]}>
            <ClientProvider>
              <EHRClientProvider>
                <ClientDashboard />
              </EHRClientProvider>
            </ClientProvider>
          </Authorize>
        ),
        children: [
          {
            path: "new",
            element: <NewClient />,
          },
          {
            path: "client-lite",
            element: <NewClientLite />,
          },
          {
            path: ":clientId",
            element: (
              <VideoSessionProviderV2>
                <ClientDetails />
              </VideoSessionProviderV2>
            ),
            children: [
              {
                path: "",
                element: <ClientBasicInfoRedirect />,
              },
              {
                path: "basic-info",
                element: <BasicInfo />,
              },
              {
                path: "programming",
                element: (
                  <EHRClientProvider>
                    <ClientProgrammingProvider>
                      <ProgrammingRedirect />
                    </ClientProgrammingProvider>
                  </EHRClientProvider>
                ),
              },
              {
                path: "programming/goal/:goalId",
                element: (
                  <EHRClientProvider>
                    <ClientProgrammingProvider>
                      <ProgrammingRedirect />
                    </ClientProgrammingProvider>
                  </EHRClientProvider>
                ),
              },
              {
                path: "programming/goal/:goalId/target/:targetId",
                element: (
                  <EHRClientProvider>
                    <ClientProgrammingProvider>
                      <ProgrammingRedirect />
                    </ClientProgrammingProvider>
                  </EHRClientProvider>
                ),
              },
              {
                path: "ehr-data",
                element: (
                  <Authorize allowedRoles={["frontera-internal"]}>
                    <ClientEhrData />
                  </Authorize>
                ),
                children: [
                  { path: "", element: <ClientEhrDataRedirect /> },
                  { path: "programming", element: <ClientEhrProgramming /> },
                  { path: "programming/unmatched", element: <ClientEhrProgramming showUnmatched /> },
                  { path: "programming/:goalId", element: <ClientEhrProgramming /> },
                  { path: "programming/:goalId/targets/:targetId", element: <ClientEhrProgramming /> },
                  { path: "sync-history", element: <ClientEhrSyncHistory /> },
                ],
              },
              {
                path: "intake-details",
                element: <ClientIntakeDetails />,
                children: [
                  {
                    path: "",
                    element: <ClientOverviewRedirect />,
                  },
                  {
                    path: "overview",
                    element: <ClientDetailsDetails />,
                  },
                  {
                    path: "availability",
                    element: <ClientDetailsAvailability />,
                  },
                  {
                    path: "insurance",
                    element: <ClientDetailsInsurance />,
                  },
                  {
                    path: "EHR-data",
                    element: <ClientDetailsEHRData />,
                  },
                  {
                    path: "files",
                    element: <ClientDetailsFiles />,
                  },
                  {
                    path: "forms",
                    element: <ClientDetailsForms />,
                    children: [
                      {
                        path: "intake_form",
                        element: <ClientDetailsFormsIntakeForm />,
                        children: [
                          {
                            path: "all",
                            element: <IntakeFormPageSwitcher />,
                          },
                          ...links.map((link) => {
                            return {
                              path: link.name,
                              element: <IntakeFormPageSwitcher />,
                              children: link.sublinks?.map((sublink) => {
                                return {
                                  path: sublink.name,
                                  element: <IntakeFormPageSwitcher />,
                                };
                              }),
                            };
                          }),
                        ],
                      },
                    ],
                  },
                ],
              },

              {
                path: "file-library",
                element: <FileLibrary />,
              },
              {
                path: "caregivers",
                element: <ClientDetailsCaregivers />,
              },
              {
                path: "providers",
                element: <ClientProviders />,
              },
            ],
          },
        ],
        errorElement: <RouteErrorElement />,
      },
      {
        path: "clinical-reports",
        element: (
          <Authorize allowedRoles={roleMapping["/clinical-reports"]}>
            <EHRClientProvider>
              <ClinicalReportsRedirect />
            </EHRClientProvider>
          </Authorize>
        ),
        errorElement: <RouteErrorElement />,
        children: [
          {
            index: true,
            element: <ClinicalReports />,
          },
          {
            path: "reassessment",
            errorElement: <RouteErrorElement />,
            children: [
              {
                path: ":reportId",
                element: (
                  <ClinicalReportDetailsProvider>
                    <ClinicalReportDetails />
                  </ClinicalReportDetailsProvider>
                ),
                errorElement: <RouteErrorElement />,
                children: [
                  {
                    path: "",
                    element: <ClinicalReportDetailsRedirect />,
                  },
                  {
                    path: "upload-files",
                    element: <ClinicalReportUploadFiles />,
                  },
                  {
                    path: "refreshing-data",
                    element: <RefreshingData />,
                  },
                  {
                    path: "select-programming",
                    element: <SelectProgramming />,
                  },
                  {
                    path: "review-learning-tree",
                    element: <LearningTreeReview />,
                  },
                  {
                    path: "report-page",
                    element: <ReportPage />,
                  },
                  {
                    path: "programming",
                    element: <ProgrammingPage />,
                  },
                  {
                    path: "narrative",
                    element: <NarrativePage />,
                  },
                  {
                    path: "review-and-sign",
                    element: <ReviewAndSign />,
                  },
                ],
              },
            ],
          },
          {
            path: "evaluations",
            errorElement: <RouteErrorElement />,
            children: [
              {
                path: ":reportId",
                element: (
                  <ClinicalReportDetailsProvider>
                    <DbSinglePageRedirect />
                  </ClinicalReportDetailsProvider>
                ),
                errorElement: <RouteErrorElement />,
                children: [
                  {
                    path: "",
                    element: <ClinicalReportDetailsRedirect />,
                  },
                  {
                    path: "upload-files",
                    element: <ClinicalReportUploadFiles />,
                  },
                  {
                    path: "report-page",
                    element: <ReportPage />,
                  },
                  {
                    path: "review-and-sign",
                    element: <ReviewAndSign />,
                  },
                ],
              },
            ],
          },
          {
            path: "assessment-reports",
            errorElement: <RouteErrorElement />,
            children: [
              {
                path: ":assessmentId",
                element: <IaClinicalMergeReportRedirect />,
                errorElement: <RouteErrorElement />,
                children: [
                  {
                    path: "",
                    element: <AssessmentReportV2DetailsRedirect />,
                  },
                  {
                    path: "client-details",
                    element: <AssessmentReportV2ClientDetails />,
                  },
                  {
                    path: "upload-files",
                    element: <UploadFilesV2 />,
                  },
                  {
                    path: "report-part-1",
                    element: <ReportSummaryPart1V2 />,
                  },
                  {
                    path: "prioritize",
                    element: <PrioritizeV2 />,
                  },
                  {
                    path: "short-term-goals",
                    element: <ShortTermGoalsV2 />,
                  },
                  {
                    path: "report-part-2",
                    element: <ReportSummaryPart2V2 />,
                  },
                  {
                    path: "review-and-sign",
                    element: <ReviewAndSignV2 />,
                  },
                ],
              },
            ],
          },
          // added to support old reassessment routes. Should be removed later.
          {
            path: "*",
            element: <ClinicalReportRedirect />,
          },
        ],
      },
      {
        path: "templates",
        element: (
          <Authorize allowedRoles={roleMapping["/templates"]}>
            <TemplateProvider>
              <TemplatesRedirect />
            </TemplateProvider>
          </Authorize>
        ),
        children: [
          {
            path: ":reportTemplateId",
            element: (
              <TemplateEditorProvider>
                <TemplateEditor />
              </TemplateEditorProvider>
            ),
          },
          {
            path: ":reportTemplateId/qa-criteria",
            element: (
              <Authorize allowedRoles={roleMapping["/qa-criteria"]}>
                <FeatureFlagGate flagKey="qa-manager">
                  <ReportTemplateQASettings />
                </FeatureFlagGate>
              </Authorize>
            ),
          },
        ],
        errorElement: <RouteErrorElement />,
      },
      {
        path: "frontera-clarity",
        element: (
          <Authorize allowedRoles={roleMapping["/frontera-clarity"]}>
            <Outlet />
          </Authorize>
        ),
        children: [
          {
            index: true,
            element: <FronteraClarity />,
          },
          {
            path: "expert-chat",
            element: <ExpertChatPage />,
            children: [
              {
                path: ":chatId",
                element: <ExpertChatPage />,
              },
            ],
          },
          {
            path: "billing",
            element: <BillingPage />,
          },
          {
            path: "scheduling",
            element: <SchedulingPage />,
          },
          {
            path: "my-availability",
            element: <MyAvailabilityPage />,
          },
        ],
      },
      {
        path: "notifications",
        children: [
          {
            path: "organization",
            element: (
              <Authorize allowedRoles={roleMapping["/notifications/organization"]}>
                <OrgManageNotificationsPage />
              </Authorize>
            ),
          },
          {
            path: "user",
            element: <UserManageNotificationsPage />,
          },
        ],
      },
      {
        path: "integrations",
        element: (
          <Authorize allowedRoles={roleMapping["/integrations"]}>
            <UserIntegrationsRedirect />
          </Authorize>
        ),
      },
      {
        path: "settings",
        children: [
          {
            path: "profile",
            element: (
              <Authorize allowedRoles={roleMapping["/settings/profile"]}>
                <ProfileSettings />
              </Authorize>
            ),
          },
          {
            path: "signature",
            element: (
              <Authorize allowedRoles={roleMapping["/settings/signature"]}>
                <SignatureSettings />
              </Authorize>
            ),
          },
        ],
      },
      {
        path: "qa-criteria",
        element: (
          <Authorize allowedRoles={roleMapping["/qa-criteria"]}>
            <FeatureFlagGate flagKey="qa-manager">
              <OrgQASettings />
            </FeatureFlagGate>
          </Authorize>
        ),
      },
      {
        path: "goal-bank",
        element: (
          <Authorize allowedRoles={roleMapping["/goal-bank"]}>
            <GoalBankImportPage />
          </Authorize>
        ),
      },
      {
        path: "leads",
        element: (
          <FeatureFlagGate flagKey="portal-care-navigation">
            <Authorize allowedRoles={roleMapping["/leads"]}>
              <Outlet />
            </Authorize>
          </FeatureFlagGate>
        ),
        children: [
          {
            index: true,
            element: <Leads />,
          },
          {
            path: ":leadId",
            element: <LeadDetail />,
          },
        ],
      },
      {
        path: "clinics",
        element: (
          <FeatureFlagGate flagKey="portal-care-navigation">
            <Authorize allowedRoles={roleMapping["/clinics"]}>
              <Outlet />
            </Authorize>
          </FeatureFlagGate>
        ),
        children: [
          {
            index: true,
            element: <Clinics />,
          },
          {
            path: ":clinicId",
            element: <ClinicDetail />,
          },
          {
            path: ":clinicId/edit",
            element: <EditClinic />,
          },
          {
            path: "add",
            element: <AddClinic />,
          },
        ],
      },
    ],
  },
  {
    path: "checkout",
    /**
     * what gets rendered at / (the Root route)
     * The rest of the routes will render inside of it
     */
    element: <Checkout />,
    errorElement: <RouteErrorElement />,
  },
]);
